import { apiClient } from '@common/http/query-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@ui/toast/toast';

// Types
export interface Avatar {
    id: number;
    avatar_group_id: number;
    name: string | null;
    image_path: string;
    image_url: string;
    order: number;
    is_active: boolean;
}

export interface AvatarGroup {
    id: number;
    name: string;
    order: number;
    is_active: boolean;
    avatars: Avatar[];
}

// Fetch all avatar groups with avatars (for user selection)
export function useAvatars() {
    return useQuery({
        queryKey: ['avatars'],
        queryFn: () => apiClient.get<{ groups: AvatarGroup[] }>('avatars').then(r => r.data.groups),
    });
}

// Fetch avatar groups for admin (datatable)
export function useAdminAvatarGroups() {
    return useQuery({
        queryKey: ['admin', 'avatar-groups'],
        queryFn: () => apiClient.get<{ pagination: { data: AvatarGroup[] } }>('admin/avatar-groups').then(r => r.data.pagination.data),
    });
}

// Create avatar group
export function useCreateAvatarGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { name: string; order?: number }) =>
            apiClient.post('admin/avatar-groups', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'avatar-groups'] });
            queryClient.invalidateQueries({ queryKey: ['avatars'] });
            toast('Grupo criado com sucesso!');
        },
        onError: () => toast.danger('Erro ao criar grupo'),
    });
}

// Update avatar group
export function useUpdateAvatarGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...data }: { id: number; name?: string; order?: number; is_active?: boolean }) =>
            apiClient.put(`admin/avatar-groups/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'avatar-groups'] });
            queryClient.invalidateQueries({ queryKey: ['avatars'] });
            toast('Grupo atualizado!');
        },
        onError: () => toast.danger('Erro ao atualizar grupo'),
    });
}

// Delete avatar group
export function useDeleteAvatarGroup() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => apiClient.delete(`admin/avatar-groups/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'avatar-groups'] });
            queryClient.invalidateQueries({ queryKey: ['avatars'] });
            toast('Grupo deletado!');
        },
        onError: () => toast.danger('Erro ao deletar grupo'),
    });
}

// Upload avatar to a group
export function useUploadAvatar() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { avatar_group_id: number; image: File; name?: string }) => {
            const formData = new FormData();
            formData.append('avatar_group_id', data.avatar_group_id.toString());
            formData.append('image', data.image);
            if (data.name) formData.append('name', data.name);
            return apiClient.post('admin/avatars', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'avatar-groups'] });
            queryClient.invalidateQueries({ queryKey: ['avatars'] });
            toast('Avatar adicionado!');
        },
        onError: () => toast.danger('Erro ao adicionar avatar'),
    });
}

// Add avatar from URL (for library selection)
export function useAddAvatarFromUrl() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data: { avatar_group_id: number; image_url: string; name?: string }) =>
            apiClient.post('admin/avatars/from-url', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'avatar-groups'] });
            queryClient.invalidateQueries({ queryKey: ['avatars'] });
            toast('Avatar adicionado!');
        },
        onError: () => toast.danger('Erro ao adicionar avatar'),
    });
}

// Delete avatar
export function useDeleteAvatar() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => apiClient.delete(`admin/avatars/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'avatar-groups'] });
            queryClient.invalidateQueries({ queryKey: ['avatars'] });
            toast('Avatar deletado!');
        },
        onError: () => toast.danger('Erro ao deletar avatar'),
    });
}

// Reorder groups
export function useReorderGroups() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (groups: { id: number; order: number }[]) =>
            apiClient.post('admin/avatar-groups/reorder', { groups }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'avatar-groups'] });
            queryClient.invalidateQueries({ queryKey: ['avatars'] });
        },
        onError: () => toast.danger('Erro ao reordenar grupos'),
    });
}

// Reorder avatars within a group
export function useReorderAvatars() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (avatars: { id: number; order: number }[]) =>
            apiClient.post('admin/avatars/reorder', { avatars }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'avatar-groups'] });
            queryClient.invalidateQueries({ queryKey: ['avatars'] });
        },
        onError: () => toast.danger('Erro ao reordenar avatares'),
    });
}

